/* eslint-disable @typescript-eslint/no-explicit-any */
 
 
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore, type User } from '../../store/authStore';
import { apiService } from '../../services/apiService';
import { API_PATHS } from '../../utils/apiPaths';
import { minimalUser, normalizeMerchantUser } from '../../utils/authMappers';
import { validateEmail } from '../../utils/validation';
import { extractApiData } from '../../utils/helpers';
import { resolveMerchantDashboardKey } from '../../utils/merchantDashboardKey';
import './Login.css';
import { BsEyeFill, BsEyeSlashFill, BsClockHistory } from 'react-icons/bs';
import Loader from '../shared/Loader';
import { AuthBrand, AuthShell } from './AuthLayout';

function isApiSuccess(body: any): boolean {
  if (!body || typeof body !== 'object') return false;
  if (body.success === true || body.success === 'true') return true;
  if (body.status === true) return true;
  if (String(body.status || '').toUpperCase() === 'SUCCESS') return true;
  if (body.code === 200 || body.statusCode === 200) return true;
  return false;
}

// function extractAccessToken(root: any): string | undefined {
//   if (typeof root === 'string' && root.length > 20) return root;
//   if (!root || typeof root !== 'object') return undefined;
//   const pools = [root, root.data, root.data?.data, root.data?.tokens, root.tokens];
//   const keys = ['access_token', 'accessToken', 'token', 'jwt', 'jwtToken', 'authToken', 'bearerToken', 'idToken'];
//   for (const obj of pools) {
//     if (typeof obj === 'string' && obj.length > 15) return obj;
//     if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
//       for (const k of keys) {
//         const v = (obj as Record<string, unknown>)[k];
//         if (typeof v === 'string' && v.length > 0) return v;
//       }
//     }
//   }
//   return undefined;
// }

// function extractUserPayload(body: any): any | null {
//   if (!body || typeof body !== 'object') return null;
//   const d = body.data ?? body;
//   const nested =
//     d?.user ??
//     d?.admin ??
//     d?.adminUser ??
//     d?.profile ??
//     d?.userDetails;
//   if (nested && typeof nested === 'object') return nested;
//   if (d && typeof d === 'object' && (d.fullName || d.emailAddress || d.email || d.name)) return d;
//   return null;
// }

function normalizeUser(raw: any, fallbackEmail: string): User {
  if (!raw || typeof raw !== 'object') return minimalUser(fallbackEmail);

  const fromParts = [raw.firstName ?? raw.first_name, raw.lastName ?? raw.last_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const fullName = String(
    raw.fullName ?? raw.name ?? fromParts ?? fallbackEmail.split('@')[0] ?? 'User'
  ).trim();
  const adminType = String(raw.adminType ?? raw.roleCode ?? raw.role ?? raw.roleName ?? '').trim();
  const email = String(raw.email ?? raw.emailAddress ?? fallbackEmail).trim();

  return {
    id: Number(raw.adminId ?? raw.id ?? raw.userId ?? 0) || 0,
    fullName,
    email,
    adminType,
    phone: String(raw.phone ?? raw.phoneNumber ?? ''),
    role: adminType,
    status: String(raw.status ?? ''),
    company: String(raw.company ?? raw.companyName ?? ''),
    department: String(raw.department ?? ''),
    userType: 'ADMIN',
    assignedMerchantIds: Array.isArray(raw.assignedMerchantIds)
      ? raw.assignedMerchantIds.map(Number)
      : undefined,
    assignedMerchantNames: Array.isArray(raw.assignedMerchantNames)
      ? raw.assignedMerchantNames.map(String)
      : undefined,
  };
}

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [loginRequestId, setLoginRequestId] = useState('');
  const [isOtpStep, setIsOtpStep] = useState(false);
  const [loginMode, setLoginMode] = useState<'admin' | 'merchant'>('admin');
  const [showPwd, setShowPwd] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuthStore();

  useEffect(() => {
    if (searchParams.get('session') === 'expired') {
      setError('Your session has expired. Please sign in again.');
    }
  }, [searchParams]);

  // Shared by both the OTP-verify success path and the "2FA is off, initiate already logged us
  // in" bypass path — builds the User object, does the merchant-phone lookup, and navigates.
  const completeLogin = async (mode: 'admin' | 'merchant', loginData: any, fallbackEmail: string) => {
    let user = mode === 'merchant'
      ? normalizeMerchantUser(loginData, fallbackEmail)
      : normalizeUser(loginData, fallbackEmail);

    if (mode === 'merchant' && user.merchantId) {
      const res = await apiService.get(`${API_PATHS.GET_MERCHANT}/${user.merchantId}`);
      const merchant = extractApiData<Record<string, unknown>>(res);
      const contactPhone = String(merchant?.contactPhone ?? '').trim();
      user = {
        ...user,
        phone: contactPhone || user.phone,
        dashboardKey: resolveMerchantDashboardKey(res),
      };
    }

    login(user, '');
    navigate('/dashboard/transaction-dashboard', { replace: true });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    try {
      if (!isOtpStep) {
        const emailError = validateEmail(email, { required: true });
        if (emailError) {
          setError(emailError);
          return;
        }

        let mode: 'admin' | 'merchant' = 'admin';
        let data = await apiService.post<any>(API_PATHS.ADMIN_LOGIN_INITIATE, {
          email,
          password,
        }).catch(() => null);

        if (!isApiSuccess(data)) {
          mode = 'merchant';
          data = await apiService.post<any>(API_PATHS.MERCHANT_LOGIN_INITIATE, {
            email,
            password,
          }).catch(() => null);
        }

        if (isApiSuccess(data)) {
          const initiateData = data?.data ?? data;

          // 2FA disabled for this user — initiateAdminLogin/initiateMerchantLogin already issued
          // a full session (and set the cookie) instead of sending an OTP. Skip the OTP screen
          // entirely and log in directly with this same response payload.
          if (initiateData?.otpRequired === false) {
            await completeLogin(mode, initiateData, email);
            return;
          }

          const requestId =
            data?.data?.loginRequestId ||
            data?.loginRequestId ||
            data?.data?.requestId ||
            data?.requestId;
          if (!requestId) {
            setError('Login request ID missing in response.');
            return;
          }

          setLoginMode(mode);
          setLoginRequestId(requestId);
          setOtp('');
          setIsOtpStep(true);
        } else {
          setError(data?.message || 'Login failed. Please check your credentials.');
        }
      } else {
        if (otp.length !== 6) {
          setError('Please enter a valid 6-digit OTP.');
          return;
        }

        const verifyPath = loginMode === 'merchant'
          ? API_PATHS.MERCHANT_LOGIN_VERIFY_OTP
          : API_PATHS.ADMIN_LOGIN_VERIFY_OTP;
        const data = await apiService.post<any>(verifyPath, {
          email,
          loginRequestId,
          otp,
        });

        if (isApiSuccess(data)) {
          const loginData = data?.data ?? data;
          await completeLogin(loginMode, loginData, email);
        } else {
          setError(data.message || 'OTP verification failed. Please try again.');
        }
      }
    } catch (err: any) {
      console.error('Login error:', err);
      setError(
        err.response?.data?.message ||
        (isOtpStep ? 'Invalid OTP. Please try again.' : 'Invalid credentials. Please try again.')
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthShell>
      <AuthBrand />

      <div className="ln-form-header">
        <h1 className="ln-form-title">Welcome back</h1>
        <p className="ln-form-sub">
          Sign in to your MoneyEU CRM account, wherever you are, whenever you need it.
        </p>
      </div>

      {error && (
        <div className="ln-error">
          <BsClockHistory />
          <span>{error}</span>
        </div>
      )}

      <form onSubmit={handleSubmit} className="ln-form">
        <label className="ln-label">
          <span>Email</span>
          <div className="ln-field">
            <input
              className="ln-input"
              type="email"
              placeholder="you@company.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={isOtpStep}
              autoComplete="email"
            />
          </div>
        </label>

        <label className="ln-label">
          <span>Password</span>
          <div className="ln-field">
            <input
              className="ln-input"
              type={showPwd ? 'text' : 'password'}
              placeholder="••••••••"
              value={password}
              onChange={e => setPassword(e.target.value)}
              required={!isOtpStep}
              disabled={isOtpStep}
              autoComplete="current-password"
            />
            <button type="button" className="ln-eye" onClick={() => setShowPwd(p => !p)} tabIndex={-1} aria-label={showPwd ? 'Hide password' : 'Show password'}>
              {showPwd ? <BsEyeSlashFill /> : <BsEyeFill />}
            </button>
          </div>
        </label>

        {isOtpStep && (
          <label className="ln-label">
            <span>Enter 6-digit OTP</span>
            <div className="ln-field">
              <input
                className="ln-input"
                type="text"
                inputMode="numeric"
                pattern="[0-9]{6}"
                placeholder="123456"
                value={otp}
                onChange={e => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                required
                maxLength={6}
                autoComplete="one-time-code"
              />
            </div>
          </label>
        )}

        {!isOtpStep && (
          <div className="ln-row-forgot">
            <Link to="/forgot-password" className="ln-forgot">Forgot password</Link>
          </div>
        )}

        <button type="submit" className="ln-submit" disabled={isLoading}>
          {isLoading
            ? <><Loader size="sm" tone="light" style={{ marginRight: 8 }} /> {isOtpStep ? 'Verifying OTP…' : 'Signing in…'}</>
            : (isOtpStep ? 'Verify OTP' : 'Login')}
        </button>

        {isOtpStep && (
          <button
            type="button"
            className="ln-submit ln-submit-ghost"
            onClick={() => {
              setIsOtpStep(false);
              setOtp('');
              setLoginRequestId('');
              setError('');
            }}
            disabled={isLoading}
          >
            Use different credentials
          </button>
        )}
      </form>

      {/* Create account entry hidden until merchant self-registration is enabled.
      <p className="ln-footer-note">
        New merchant? <Link to="/register" className="ln-forgot">Create an account</Link>
      </p>
      */}
    </AuthShell>
  );
};

export default Login;

